import { useUserSession } from '@solinkcorp/authentication';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  showToast,
  Textarea,
} from '@solinkcorp/solink-ui';
import { Lock, Users } from 'lucide-react';
import AnalyticsLogger, { type InvestigationCreateSource } from 'models/AnalyticsLogger';
import { useCallback, useEffect, useState, type FC } from 'react';
import { isInvestigationCreator } from 'src/hooks/investigations/isInvestigationCreator';
import { useCreateInvestigation } from 'src/hooks/investigations/useCreateInvestigation';
import type {
  Investigation,
  InvestigationVisibility,
} from 'src/hooks/investigations/useInvestigations';
import { useUpdateInvestigation } from 'src/hooks/investigations/useUpdateInvestigation';
import useLocale from 'src/hooks/useLocale';
import { investigationUpdateChanges } from './investigationAnalytics';

interface CreateInvestigationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing?: Investigation;
  /** Called after a successful create (not edit), before the dialog closes. */
  onCreated?: (investigation: Pick<Investigation, 'id' | 'title'>) => void | Promise<void>;
  /** Where a new investigation was started. Ignored while editing. */
  source?: InvestigationCreateSource;
}

type Visibility = InvestigationVisibility;

export const CreateInvestigationModal: FC<CreateInvestigationModalProps> = ({
  open,
  onOpenChange,
  editing,
  onCreated,
  source = 'list',
}) => {
  const locale = useLocale();
  const t = locale.investigations;
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<Visibility>('public');

  const { createInvestigation } = useCreateInvestigation();
  const { updateInvestigation } = useUpdateInvestigation();
  const session = useUserSession();

  const isEditing = !!editing;
  const isCreator = !isEditing || isInvestigationCreator(editing?.createdBy, session);

  // Seed only when the dialog opens so SWR revalidation of `editing` does not
  // wipe in-progress title / description / visibility edits.
  useEffect(() => {
    if (!open) return;
    setTitle(editing?.title ?? '');
    setDescription(editing?.description ?? '');
    setVisibility(editing?.visibility ?? 'public');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: open transition only
  }, [open]);

  const handleSave = useCallback(async () => {
    if (!title.trim()) return;
    try {
      if (isEditing && editing) {
        const next = {
          title: title.trim(),
          description: description.trim(),
          visibility,
        };
        await updateInvestigation({
          id: editing.id,
          ...next,
        });
        const changes = investigationUpdateChanges(editing, next);
        if (changes) AnalyticsLogger.investigationUpdated(changes);
        showToast.success({ title: t.create.toastUpdated });
      } else {
        const created = await createInvestigation({
          title: title.trim(),
          description: description.trim(),
          visibility,
        });
        AnalyticsLogger.investigationCreated({
          visibility,
          hasDescription: description.trim().length > 0,
          source,
        });
        // Caller owns success messaging when composing create+add (picker flow).
        if (onCreated) {
          await onCreated(created);
        } else {
          showToast.success({ title: t.create.toastCreated });
        }
      }
      onOpenChange(false);
    } catch (err) {
      showToast.error({
        title: err instanceof Error ? err.message : t.create.toastSaveError,
      });
    }
  }, [
    title,
    description,
    visibility,
    isEditing,
    editing,
    createInvestigation,
    updateInvestigation,
    source,
    onCreated,
    onOpenChange,
    t.create,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="tw overflow-hidden">
        <DialogHeader>
          <DialogTitle>{isEditing ? t.editInvestigation : t.newInvestigation}</DialogTitle>
        </DialogHeader>

        <div className="tw flex min-w-0 flex-col gap-4 pt-2">
          {/* Visibility segmented control */}
          <div
            className={`tw flex overflow-hidden rounded-lg border border-border ${!isCreator ? 'tw pointer-events-none opacity-50' : ''}`}
          >
            <button
              type="button"
              disabled={!isCreator}
              onClick={() => setVisibility('public')}
              className={`tw flex flex-1 items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors ${
                visibility === 'public'
                  ? 'tw bg-background-elevated text-foreground-primary'
                  : 'tw bg-background-base text-foreground-secondary hover:bg-background-elevated'
              }`}
            >
              <Users className="tw size-4" />
              {locale.general.public}
            </button>
            <div className="tw w-px shrink-0 bg-border" />
            <button
              type="button"
              disabled={!isCreator}
              onClick={() => setVisibility('private')}
              className={`tw flex flex-1 items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors ${
                visibility === 'private'
                  ? 'tw bg-background-elevated text-foreground-primary'
                  : 'tw bg-background-base text-foreground-secondary hover:bg-background-elevated'
              }`}
            >
              <Lock className="tw size-4" />
              {locale.general.private}
            </button>
          </div>

          <div className="tw flex min-w-0 flex-col gap-1.5">
            <label className="tw text-sm font-medium text-foreground-primary">
              {t.create.nameRequired}
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t.create.namePlaceholder}
              className="tw"
            />
          </div>

          <div className="tw flex min-w-0 flex-col gap-1.5">
            <label className="tw text-sm font-medium text-foreground-primary">
              {t.create.description}
            </label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t.create.descriptionPlaceholder}
              rows={4}
              className="tw min-w-0 max-w-full [field-sizing:fixed] [overflow-wrap:anywhere]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="tw">
            {locale.general.cancel}
          </Button>
          <Button
            onClickAsync={handleSave}
            disabled={!title.trim()}
            loadingText={t.create.saving}
            className="tw"
          >
            {locale.general.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
